import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { apiErrorCode, crmErrorMessage } from "../api/crmErrors";
import { IMPORT_ACCEPT, IMPORT_FIELDS, IMPORT_FIELD_KEYS, IMPORT_MAX_ROWS, IMPORT_SKIP as SKIP, STAGES, STAGE_KEYS } from "../contract";
import { fmtCount } from "../format";
import { crmHref } from "../paths";
import { STEPS, canAdvance, importReducer, initialImportState } from "../import/importReducer";
import CrmPageHeader from "../ui/CrmPageHeader";
import Wizard from "../ui/Wizard";
import Dropzone from "../ui/Dropzone";
import CrmSelect from "../ui/CrmSelect";
import CrmSwitch from "../ui/CrmSwitch";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import { CRM } from "../../../constants/testIds/crm";

const DUPLICATE_PAGE = 50;
const STEP_KEYS = Object.freeze({
  upload: "import.stepUpload",
  map: "import.stepMap",
  duplicates: "import.stepDuplicates",
  consent: "import.stepConsent",
  confirm: "import.stepConfirm",
});

/** Step 1 · Upload: drop or browse a CSV/XLSX; filename and row count once read. */
function UploadStep({ job, error, busy, onFile }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  return (
    <div className="space-y-3">
      <Dropzone prompt={t("import.drop")} browseLabel={t("import.browse")} accept={IMPORT_ACCEPT} disabled={busy} onFile={onFile}
        testId={CRM.importDropzone} inputTestId={CRM.importFile} />
      {job && (
        <p data-testid={CRM.importDetected} className="text-sm text-eh-ink">
          {job.filename} · <strong>{t("import.rowsDetected", { n: fmtCount(job.row_count, lang) })}</strong>
        </p>
      )}
      {error && <p role="alert" data-testid={CRM.importError} className="text-sm text-red-700">{error}</p>}
      <p className="text-xs text-eh-muted">{t("import.max", { n: fmtCount(IMPORT_MAX_ROWS, lang) })}</p>
    </div>
  );
}

/** Step 2 · Map columns: file column → CRM field; "✓" marks an auto-detected choice. */
function MapStep({ job, mapping, onMap }) {
  const t = useT("crm");
  const suggested = job?.suggested_mapping || {};
  const options = IMPORT_FIELDS.map((f) => ({ value: f, label: t(IMPORT_FIELD_KEYS[f]) }));
  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {(job?.columns || []).map((col) => {
          const key = String(col.index);
          const value = mapping[key] || SKIP;
          const auto = suggested[key] && suggested[key] === mapping[key];
          return (
            <li key={key} className="flex flex-wrap items-center gap-3 text-sm text-eh-ink">
              <span className="w-40 truncate" title={col.samples?.filter(Boolean).join(", ")}>{col.header}</span>
              <span aria-hidden="true">→</span>
              <CrmSelect aria-label={t("import.mapLabel", { column: col.header })} value={value}
                onChange={(e) => onMap(key, e.target.value === SKIP ? "" : e.target.value)} options={options}
                selectClassName={value === SKIP ? "text-eh-muted" : ""} data-testid={CRM.importMap(key)} />
              {auto && <span className="text-eh-ink" aria-label={t("import.autoDetected")}>✓</span>}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-eh-muted">{t("import.mapHint")}</p>
    </div>
  );
}

/** Step 3 · Duplicates: rows matching existing clients by email; Merge all / Skip all, or per row. */
function DuplicatesStep({ jobId, mode, decisions, onMode, onDecide }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    crmApi.listImportDuplicates(jobId, { page: 1, pageSize: DUPLICATE_PAGE }).then(setPage).catch(setError);
  }, [jobId]);
  useEffect(() => { load(); }, [load]);
  const decisionOf = (row) => decisions[String(row.row_index)] || (mode === "skip_all" ? "skip" : "merge");
  if (error) return <CrmLoadFailure error={error} onRetry={load} />;
  if (!page) return <div className="h-20 animate-pulse rounded-2xl bg-eh-cream" aria-busy="true" />;
  return (
    <div className="space-y-3">
      <p className="text-sm text-eh-ink">{t("import.duplicates", { n: fmtCount(page.total, lang) })}</p>
      <div className="flex gap-2">
        <CrmButton size="sm" variant={mode === "merge_all" ? "primary" : "outline"} disabled={page.total === 0} onClick={() => onMode("merge_all")} data-testid={CRM.importMergeAll}>{t("import.mergeAll")}</CrmButton>
        <CrmButton size="sm" variant={mode === "skip_all" ? "primary" : "outline"} disabled={page.total === 0} onClick={() => onMode("skip_all")} data-testid={CRM.importSkipAll}>{t("import.skipAll")}</CrmButton>
      </div>
      <ul className="space-y-1.5">
        {page.rows.map((row) => (
          <li key={row.row_index} className="flex flex-wrap items-center justify-between gap-2 text-sm text-eh-ink">
            <span className="min-w-0 truncate" title={row.existing_name}>{row.email}</span>
            <span className="flex gap-3">
              {["merge", "skip"].map((d) => (
                <label key={d} className="inline-flex items-center gap-1.5">
                  <input type="radio" name={`crm-dup-${row.row_index}`} checked={decisionOf(row) === d} onChange={() => onDecide(row.row_index, d)}
                    data-testid={CRM.importRow(row.row_index, d)} className="h-4 w-4 accent-eh-sage-deep" />
                  {t(d === "merge" ? "import.merge" : "import.skip")}
                </label>
              ))}
            </span>
          </li>
        ))}
      </ul>
      {page.total > page.rows.length && <p className="text-xs text-eh-muted">{t("import.duplicatesMore", { n: fmtCount(page.total - page.rows.length, lang) })}</p>}
    </div>
  );
}

/** Step 4 · Consent: the two declarations; the import proceeds either way. */
function ConsentStep({ consent, onChange }) {
  const t = useT("crm");
  const rows = [
    { key: "previously_emailed", id: "received", label: t("import.consentReceived") },
    { key: "has_permission", id: "permission", label: t("import.consentPermission") },
  ];
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <label key={r.key} className="flex items-start gap-3 text-sm text-eh-ink">
          <CrmSwitch checked={!!consent[r.key]} onCheckedChange={(v) => onChange(r.key, v)} data-testid={CRM.importConsent(r.id)} aria-label={r.label} />
          <span>{r.label}</span>
        </label>
      ))}
      <p className="text-xs text-eh-muted">{t("import.consentHint")}</p>
    </div>
  );
}

/** Step 5 · Confirm: "1,187 new · 38 merged · 15 skipped", the entry stage, Start import. */
function ConfirmStep({ summary, stage, onStage, onStart, busy }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  return (
    <div className="space-y-4">
      {summary ? (
        <p data-testid={CRM.importSummary} className="text-lg text-eh-ink">
          {t("import.summary", { new: fmtCount(summary.created, lang), merged: fmtCount(summary.merged, lang), skipped: fmtCount(summary.skipped, lang) })}
        </p>
      ) : <div className="h-7 w-64 animate-pulse rounded-full bg-eh-cream" aria-busy="true" />}
      <label className="flex flex-wrap items-center gap-2 text-sm text-eh-ink">
        <span>{t("import.enterAs")}</span>
        <CrmSelect value={stage} onChange={(e) => onStage(e.target.value)} options={STAGES.map((s) => ({ value: s, label: t(STAGE_KEYS[s]) }))}
          data-testid={CRM.importStage} />
      </label>
      <CrmButton onClick={onStart} disabled={busy || !summary} data-testid={CRM.importStart}>{t("import.start")}</CrmButton>
    </div>
  );
}

/**
 * Admin → CRM → Import (spec Screen 5): five steps, Back/Next throughout. Each step's
 * choice is saved to the server as the practitioner moves past it, so `?job=<id>`
 * resumes an unfinished import at Map columns. Start runs the import in the background
 * and returns to Clients, where the banner reports progress and the result.
 */
export default function CrmImport() {
  const t = useT("crm");
  const tc = useT("common");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [state, dispatch] = useReducer(importReducer, initialImportState);
  const [uploadError, setUploadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resumeError, setResumeError] = useState(null);
  const resumed = useRef(false);
  const jobId = state.job?.id;

  useEffect(() => {
    const id = params.get("job");
    if (!id || resumed.current) return;
    resumed.current = true;
    crmApi.getImport(id)
      .then((job) => { if (job?.status === "draft") dispatch({ type: "resume", job, step: 1 }); else navigate(crmHref("/clients"), { replace: true }); })
      .catch(setResumeError);
  }, [params, navigate]);

  const upload = async (file) => {
    setUploadError("");
    setBusy(true);
    try {
      const job = await crmApi.createImport(file);
      dispatch({ type: "uploaded", job });
      setParams((prev) => { const next = new URLSearchParams(prev); next.set("job", job.id); return next; }, { replace: true });
      resumed.current = true;
    } catch (e) {
      const code = apiErrorCode(e);
      setUploadError(code ? crmErrorMessage(e, t, tc, "errors.importUnreadable") : t("errors.importFailed"));
    } finally {
      setBusy(false);
    }
  };

  // A refusal on any step is stated INSIDE the wizard, next to the step it concerns
  // (IMPORT_MAPPING_INVALID, IMPORT_NOT_MAPPED, IMPORT_NOT_COMPLETED, …), not in a toast.
  const [stepError, setStepError] = useState("");
  const save = async (fn) => {
    setBusy(true);
    setStepError("");
    try { await fn(); return true; } catch (e) { setStepError(crmErrorMessage(e, t, tc)); return false; } finally { setBusy(false); }
  };

  const next = async () => {
    const step = STEPS[state.step];
    let ok = true;
    if (step === "map") ok = await save(async () => { const job = await crmApi.saveImportMapping(jobId, state.mapping); if (job) dispatch({ type: "job", job }); });
    if (step === "duplicates") ok = await save(() => crmApi.saveImportDuplicates(jobId, { mode: state.mode, decisions: state.decisions }));
    if (step === "consent") {
      ok = await save(() => crmApi.saveImportConsent(jobId, state.consent));
      if (ok) {
        dispatch({ type: "summary", summary: null });
        crmApi.getImportSummary(jobId).then((summary) => dispatch({ type: "summary", summary })).catch((e) => setStepError(crmErrorMessage(e, t, tc, "errors.loadFailed")));
      }
    }
    if (ok) dispatch({ type: "goto", step: state.step + 1 });
  };

  const start = async () => {
    const ok = await save(() => crmApi.startImport(jobId, state.stage));
    if (ok) {
      toast.success(t("import.started"));
      navigate(crmHref("/clients"));
    }
  };

  const steps = STEPS.map((key) => ({ key, label: t(STEP_KEYS[key]) }));
  const body = {
    upload: <UploadStep job={state.job} error={uploadError} busy={busy} onFile={upload} />,
    map: <MapStep job={state.job} mapping={state.mapping} onMap={(column, field) => { setStepError(""); dispatch({ type: "map", column, field }); }} />,
    duplicates: jobId && <DuplicatesStep jobId={jobId} mode={state.mode} decisions={state.decisions}
      onMode={(mode) => dispatch({ type: "mode", mode })} onDecide={(row, decision) => dispatch({ type: "decide", row, decision })} />,
    consent: <ConsentStep consent={state.consent} onChange={(key, value) => dispatch({ type: "consent", key, value })} />,
    confirm: <ConfirmStep summary={state.summary} stage={state.stage} busy={busy} onStage={(stage) => dispatch({ type: "stage", stage })} onStart={start} />,
  }[STEPS[state.step]];

  return (
    <div data-testid={CRM.import} className="space-y-4">
      <CrmPageHeader title={t("import.title")} subtitle={t("import.intro")} className="mb-0" />
      {resumeError && <CrmLoadFailure error={resumeError} />}
      <Wizard
        steps={steps}
        index={state.step}
        onBack={() => { setStepError(""); dispatch({ type: "goto", step: state.step - 1 }); }}
        onNext={next}
        canNext={canAdvance(state)}
        busy={busy}
        nextHint={state.step === 1 ? t("import.nextHint") : null}
        ids={{ step: CRM.importStep, next: CRM.importNext, back: CRM.importBack }}
      >
        {body}
        {stepError && state.step > 0 && <p role="alert" data-testid={CRM.importError} className="mt-4 text-sm text-red-700">{stepError}</p>}
      </Wizard>
    </div>
  );
}
